import { BoxEntity } from "@modules/box/entities/boxEntity"
import {
  type CouponInputFormik,
  DurationType,
  type PauseInputFormik,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { CouponEntity } from "@modules/common/couponObject/entities/couponObjectEntity"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PauseEntity } from "@modules/common/pauseObject/entities/pauseObjectEntity"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { TrialSubscriptionEntity } from "@modules/common/trialSubscription/entities/trailSubscriptionEntity"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import { LightInvoiceEntity } from "@modules/finances/entities/lightInvoiceEntity"
import { Language } from "@modules/language/utils/languageUtils"
import { OnOffProgramProgressionEntity } from "@modules/programs/entities/onOffProgramProgressionEntity"
import { type ProgramSubscriberDto } from "@modules/programs/programSubscriber/gateway/programSubscriberDto"
import { ProgramProgressionStatus } from "@modules/programs/types/programOnOffTypes"
import { ProgramSubscriptionStatus } from "@modules/programs/types/programTypes"
import { LightPromoCodeEntity } from "@modules/promoCode/entities/lightPromoCodeEntity"
import { type HUImage } from "@modules/types/imageTypes"
import { colors } from "@modules/ui/styles/colors"
import dayjs from "dayjs"

export type ProgramSubscriberState = Omit<
  ProgramSubscriberEntity,
  | "getProgramSubscriptionStatusInfos"
  | "getPause"
  | "canSubscriptionBePaused"
  | "canApplyCoupon"
  | "canDeleteSubscriber"
  | "getCoupon"
  | "getWordingCouponPriceAfterDiscount"
  | "getWordingReductionForDiscount"
  | "getWordingReductionForPromoCode"
  | "getWordingCouponDate"
  | "getWordingTrialDate"
  | "getWordingPause"
  | "getWordingPrice"
  | "getProgramSubscriptionOnOffStatusInfos"
>

export class ProgramSubscriberEntity {
  id: string | null
  username: string | null
  firstname: string | null
  lastname: string | null
  avatar?: HUImage
  subscriptionStatus: ProgramSubscriptionStatus
  periodEndDate: Date | null
  subscriptionId: string
  subscriptionDate: Date
  paidPrice: PriceObjectEntity | null
  referencePrice: PriceObjectEntity | null
  usedPromoCode?: LightPromoCodeEntity
  coupon?: CouponEntity
  pause?: PauseEntity
  trial?: TrialSubscriptionEntity
  internalNote?: string
  email?: string
  nationality?: string
  onOffProgramProgression: OnOffProgramProgressionEntity | null
  lastInvoices: LightInvoiceEntity[] | null
  nextPayment: PriceObjectEntity | null
  nextPaymentDate: Date | null
  box: BoxEntity | null

  private constructor(programSubscriber: ProgramSubscriberState) {
    this.id = programSubscriber.id
    this.username = programSubscriber.username
    this.firstname = programSubscriber.firstname
    this.lastname = programSubscriber.lastname
    this.avatar = programSubscriber.avatar
    this.subscriptionStatus = programSubscriber.subscriptionStatus
    this.periodEndDate = programSubscriber.periodEndDate
    this.subscriptionId = programSubscriber.subscriptionId
    this.subscriptionDate = programSubscriber.subscriptionDate
    this.paidPrice = programSubscriber.paidPrice
    this.referencePrice = programSubscriber.referencePrice
    this.usedPromoCode = programSubscriber.usedPromoCode
    this.coupon = programSubscriber.coupon
    this.pause = programSubscriber.pause
    this.trial = programSubscriber.trial
    this.internalNote = programSubscriber.internalNote
    this.email = programSubscriber.email
    this.nationality = programSubscriber.nationality
    this.onOffProgramProgression = programSubscriber.onOffProgramProgression
    this.lastInvoices = programSubscriber.lastInvoices
    this.nextPayment = programSubscriber.nextPayment
    this.nextPaymentDate = programSubscriber.nextPaymentDate
    this.box = programSubscriber.box
  }

  static fromDto(dto: ProgramSubscriberDto): ProgramSubscriberEntity {
    return new ProgramSubscriberEntity({
      id: dto.id,
      username: dto.username,
      firstname: dto.firstname,
      lastname: dto.lastname,
      avatar: dto.avatar ? convertImageFromDto(dto.avatar) : undefined,
      subscriptionStatus: dto.subscriptionStatus ?? ProgramSubscriptionStatus.INACTIVE,
      subscriptionId: dto.subscriptionId,
      subscriptionDate: new Date(dto.subscriptionDate),
      periodEndDate: dto.periodEndDate ? new Date(dto.periodEndDate) : null,
      paidPrice: dto.paidPrice ? PriceObjectEntity.fromDto(dto.paidPrice) : null,
      referencePrice: dto.referencePrice ? PriceObjectEntity.fromDto(dto.referencePrice) : null,
      usedPromoCode: dto.usedPromoCode ? LightPromoCodeEntity.fromDto(dto.usedPromoCode) : undefined,
      coupon: dto.coupon ? CouponEntity.fromDto(dto.coupon) : undefined,
      pause: dto.pause ? PauseEntity.fromDto(dto.pause) : undefined,
      trial: dto.trial ? TrialSubscriptionEntity.fromDto(dto.trial) : undefined,
      email: dto.email ?? undefined,
      nationality: dto.nationality ?? undefined,
      internalNote: dto.internalNote ?? undefined,
      lastInvoices: dto.lastInvoices ? dto.lastInvoices.map((invoice) => LightInvoiceEntity.fromDto(invoice)) : null,
      nextPayment: dto.nextPayment ? PriceObjectEntity.fromDto(dto.nextPayment) : null,
      nextPaymentDate: dto.nextPaymentDate ? new Date(dto.nextPaymentDate) : null,
      onOffProgramProgression: dto.onOffProgramProgressionDto
        ? OnOffProgramProgressionEntity.fromDto(dto.onOffProgramProgressionDto)
        : null,
      box: dto.box ? BoxEntity.fromDto(dto.box) : null,
    })
  }

  getWordingCouponDate(): string {
    if (!this.coupon) return ""

    return this.coupon.getWordingDate()
  }

  // The coupon is calculated after calculating the promo code if there is one
  getWordingCouponPriceAfterDiscount(language: Language): string {
    if (!this.referencePrice || !this.coupon) {
      return ""
    }

    let price = this.referencePrice.value
    if (this.usedPromoCode) {
      price =
        this.usedPromoCode.type === CouponType.PERCENT
          ? price - (price * this.usedPromoCode.value) / 100
          : price - this.usedPromoCode.value
    }

    price =
      this.coupon.type === CouponType.PERCENT ? price - (price * this.coupon.value) / 100 : price - this.coupon.value

    return formatCurrency(price, this.referencePrice.currency, language)
  }

  getWordingReductionForDiscount(language: Language): string {
    if (!this.referencePrice || !this.coupon) {
      return ""
    }

    let price = this.referencePrice.value
    if (this.usedPromoCode) {
      price =
        this.usedPromoCode.type === CouponType.PERCENT
          ? price - (price * this.usedPromoCode.value) / 100
          : price - this.usedPromoCode.value
    }

    const amountOfDiscount =
      this.coupon.type === CouponType.PERCENT ? (price * this.coupon.value) / 100 : this.coupon.value

    return formatCurrency(amountOfDiscount, this.referencePrice.currency, language)
  }

  getWordingReductionForPromoCode(language: Language): string {
    if (!this.referencePrice || !this.usedPromoCode) {
      return ""
    }

    const amountOfDiscount =
      this.usedPromoCode.type === CouponType.PERCENT
        ? (this.referencePrice.value * this.usedPromoCode.value) / 100
        : this.usedPromoCode.value

    return formatCurrency(amountOfDiscount, this.referencePrice.currency, language)
  }

  getProgramSubscriptionOnOffStatusInfos(): { color: string; label: string; information?: string } {
    if (!this.onOffProgramProgression) {
      return { color: "var(--textError)", label: word("program.subscribers.subscriptionDetails.status.inactive.label") }
    }

    switch (this.onOffProgramProgression.status) {
      case ProgramProgressionStatus.NOT_STARTED:
        return {
          color: "var(--textDefault-placeholder)",
          label: word("program.subscribers.subscriptionDetails.status.not_started.label"),
        }
      case ProgramProgressionStatus.COMPLETED:
        return {
          color: "var(--textSuccess)",
          label: word("program.subscribers.subscriptionDetails.status.completed.label"),
        }
      case ProgramProgressionStatus.STARTED:
        return {
          color: "var(--textSuccess)",
          label: word("program.subscribers.subscriptionDetails.status.started.label"),
          information: this.onOffProgramProgression.getWordingStarted(),
        }
      case ProgramProgressionStatus.STOPPED:
        return {
          color: "var(--primary-500)",
          label: word("program.subscribers.subscriptionDetails.status.stopped.label"),
          information: this.onOffProgramProgression.getWordingPause(),
        }
      default:
        return {
          color: "var(--textError)",
          label: word("program.subscribers.subscriptionDetails.status.inactive.label"),
        }
    }
  }

  getProgramSubscriptionStatusInfos(): { color: string; label: string; information?: string } {
    switch (this.subscriptionStatus) {
      case ProgramSubscriptionStatus.ACTIVE:
      case ProgramSubscriptionStatus.ACTIVE_BUNDLE:
        return { color: colors.green500, label: word("program.subscribers.subscriptionDetails.status.active.label") }
      case ProgramSubscriptionStatus.INACTIVE:
      case ProgramSubscriptionStatus.INACTIVE_BUNDLE:
        return {
          color: colors.red500,
          label: `${word("program.subscribers.subscriptionDetails.status.inactive.label")} - ${dayjs(this.periodEndDate).format("L")}`,
        }
      case ProgramSubscriptionStatus.PENDING_CANCELLATION:
        return {
          color: colors.red500,
          label: `${word("program.subscribers.subscriptionDetails.status.pending_cancellation.label")} - ${dayjs(this.periodEndDate).format("L")}`,
        }
      case ProgramSubscriptionStatus.PENDING_PAYMENT:
        return {
          color: colors.red500,
          label: word("program.subscribers.subscriptionDetails.status.pending_payment.label"),
        }
      case ProgramSubscriptionStatus.PAUSE:
        return {
          color: colors.yellow500,
          label: `${word("program.subscribers.subscriptionDetails.status.paused.label")}`,
          information: this.getWordingPause(),
        }
      case ProgramSubscriptionStatus.PAYMENT_IN_ERROR:
        return {
          color: colors.red500,
          label: word("program.subscribers.subscriptionDetails.status.payment_in_error.label"),
          information: word("program.subscribers.subscriptionDetails.status.payment_in_error.information"),
        }
      case ProgramSubscriptionStatus.TRIALING:
        return {
          color: colors.green500,
          label: word("program.subscribers.subscriptionDetails.status.trialing.label"),
          information: this.getWordingTrialDate(),
        }
      case ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION:
        return {
          color: colors.red500,
          label: word("program.subscribers.subscriptionDetails.status.trial_pending_cancellation.label"),
          information: this.getWordingTrialDate(),
        }
      case ProgramSubscriptionStatus.PENDING_REQUEST:
        return {
          color: colors.red500,
          label: word("program.subscribers.subscriptionDetails.status.pending_request.label"),
        }
    }
  }

  getWordingTrialDate(): string {
    if (!this.trial) return ""

    const totalDays = dayjs(this.trial.endDate).diff(dayjs(this.trial.startDate), "days")
    const remainingDays = dayjs(this.trial.endDate).diff(dayjs(), "days")
    const daysUsed = totalDays - remainingDays

    return word("program.subscribers.subscriptionDetails.status.trialing.information", {
      daysUsed,
      totalDays,
      date: dayjs(this.trial.endDate).format("L"),
    })
  }

  getWordingPause(): string {
    if (!this.pause) return ""

    const pausedAt = dayjs(this.pause.pausedAt).format("L")

    if (!this.pause.resumeAt) {
      return word("program.subscribers.subscriptionDetails.status.paused.unlimited", {
        date: dayjs(this.pause.pausedAt).format("L"),
      })
    }

    const totalMonths = Math.ceil(dayjs(this.pause.resumeAt).diff(dayjs(this.pause.pausedAt), "month", true))
    const monthsUsed = dayjs(this.pause.pausedAt).diff(dayjs(), "month")
    const resumeAt = dayjs(this.pause.resumeAt).format("L")
    return word("program.subscribers.subscriptionDetails.status.paused.limited", {
      monthsUsed,
      totalMonths,
      pausedAt,
      resumeAt,
    })
  }

  getWordingPrice(language: string): string {
    if (this.paidPrice === null && this.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE) {
      return word("program.price.free")
    }

    switch (this.subscriptionStatus) {
      case ProgramSubscriptionStatus.PENDING_CANCELLATION:
      case ProgramSubscriptionStatus.PAUSE:
      case ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION:
      case ProgramSubscriptionStatus.PAYMENT_IN_ERROR:
        return word("program.price.noFuturePayment")
      case ProgramSubscriptionStatus.TRIALING:
        if (this.nextPayment && this.nextPayment.value >= 0)
          return formatCurrency(this.nextPayment.value, this.nextPayment.currency, language)
        return word("program.price.free")
      default:
        return ""
    }
  }

  getPause(): PauseInputFormik {
    if (!this.pause) {
      return {
        durationType: DurationType.MONTHLY,
        months: 1,
      }
    }

    if (this.pause.resumeAt === null && this.pause.pausedAt) {
      return {
        durationType: DurationType.UNLIMITED,
        months: 0,
      }
    }

    const pausedAt = dayjs(this.pause.pausedAt)
    const resumeAt = dayjs(this.pause.resumeAt)
    const months = resumeAt.diff(pausedAt, "month")

    return {
      durationType: months > 0 ? DurationType.MONTHLY : DurationType.UNLIMITED,
      months: months > 0 ? months : 1,
    }
  }

  getCoupon(): CouponInputFormik {
    if (!this.coupon) {
      return {
        type: CouponType.PERCENT,
        value: 0,
        durationType: DurationType.MONTHLY,
        months: 0,
      }
    }
    return {
      type: this.coupon.type,
      value: this.coupon.value,
      durationType: this.coupon.duration ? DurationType.MONTHLY : DurationType.UNLIMITED,
      months: this.coupon.duration || null,
    }
  }

  canSubscriptionBePaused(): boolean {
    return (
      !!this.paidPrice &&
      !this.onOffProgramProgression &&
      (this.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PAUSE ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT)
    )
  }

  canApplyCoupon(): boolean {
    return (
      !!this.paidPrice &&
      !this.onOffProgramProgression &&
      (this.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PAUSE ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PAYMENT_IN_ERROR ||
        this.subscriptionStatus === ProgramSubscriptionStatus.TRIALING)
    )
  }

  canDeleteSubscriber(): boolean {
    if (this.onOffProgramProgression) {
      return false
    }
    return true
  }
}
